import styled from '@emotion/styled';

import { forMobile, scrollbarStyles } from '@/design-system';

import { pipelineColors } from '../../constants';
import { PipelineBadge } from '../../styled';

export const BoardShell = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  minHeight: 0,
  height: '100%',
});

export const BoardScroller = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  gap: 6,
  minHeight: 0,
  height: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  ...scrollbarStyles,
  paddingBottom: 16,

  [forMobile]: {
    width: '100%',
    overflow: 'visible',
    paddingBottom: 0,
    height: 'auto',
  },
});

export const TerminalDivider = styled('div')(({ theme }) => ({
  width: 12,
  minWidth: 12,
  display: 'flex',
  justifyContent: 'center',
  padding: '0 4px',
  boxSizing: 'border-box',

  '&::before': {
    content: '""',
    width: 1,
    height: '100%',
    borderLeft: `1px dashed ${theme.colors.border.subtle}`,
  },

  [forMobile]: {
    display: 'none',
  },
}));

export const StageTabs = styled('div')({
  display: 'none',

  [forMobile]: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    paddingBottom: 2,
    width: '100%',
  },
});

export const StageTab = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 36,
  padding: '8px',
  borderRadius: 8,
  border: `1px solid ${theme.colors.white[8]}`,
  background: theme.colors.white[4],
  color: theme.colors.white[60],
  whiteSpace: 'nowrap',
  font: 'inherit',
  cursor: 'pointer',

  '&[data-active="true"]': {
    borderColor: 'var(--stage-color)',
    color: 'var(--stage-color)',
  },
}));

export const StageTabCount = styled('span')(({ theme }) => ({
  minWidth: 16,
  height: 16,
  padding: '0 5px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 6,
  background: theme.colors.white[8],
  color: 'inherit',
  fontSize: 10,
  lineHeight: '15px',
  fontWeight: 600,
  boxSizing: 'border-box',
}));

export const ColumnShell = styled('section')({
  width: 320,
  minWidth: 320,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 16,
  borderRadius: 8,
  boxSizing: 'border-box',
  background: pipelineColors.activeColumnSurface,
  border: `1px solid ${pipelineColors.activeColumnBorder}`,

  '&[data-terminal="true"]': {
    background: pipelineColors.terminalColumnSurface,
    borderColor: pipelineColors.terminalColumnBorder,
  },

  [forMobile]: {
    width: '100%',
    minWidth: 0,
    height: 764,
    minHeight: 764,
    padding: 12,
  },
});

export const CompactColumnShell = styled('button')(({ theme }) => ({
  width: 48,
  minWidth: 48,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  padding: '16px 14px',
  borderRadius: 8,
  boxSizing: 'border-box',
  cursor: 'pointer',
  border: '1px solid var(--stage-compact-border)',
  background: 'var(--stage-compact-surface)',
  color: theme.colors.white[80],
  font: 'inherit',

  '&[data-terminal="true"]': {
    background: pipelineColors.terminalColumnSurface,
    borderColor: pipelineColors.terminalColumnBorder,
  },
  '&:hover': {
    borderColor: theme.colors.white[20],
  },

  [forMobile]: {
    display: 'none',
  },
}));

export const CompactColumnDropZone = styled('div')(({ theme }) => ({
  width: 48,
  minWidth: 48,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',

  '&[data-dragging-over="true"] > button': {
    borderColor: 'var(--stage-color)',
    boxShadow: `0 0 0 1px var(--stage-color), 0 0 0 4px ${theme.colors.white[8]}`,
  },

  [forMobile]: {
    display: 'none',
  },
}));

export const CompactColumnContent = styled('span')({
  minHeight: 46,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  textAlign: 'left',
});

export const CompactColumnLabel = styled('span')({
  color: 'var(--stage-color)',
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: '16.5px',
  letterSpacing: 0,
  textTransform: 'uppercase',
});

export const ColumnHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  minHeight: 24,
});

export const ColumnHeaderTitle = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
});

export const ColumnHeaderActions = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 16,
  flexShrink: 0,
  color: theme.colors.white[60],

  button: {
    width: 16,
    height: 16,
    padding: 0,
    color: theme.colors.white[60],
  },

  svg: {
    width: 16,
    height: 16,
  },
}));

export const CountBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 16,
  height: 16,
  padding: '0 5px',
  borderRadius: 6,
  background: theme.colors.white[8],
  color: 'var(--stage-color)',
  fontSize: 10,
  lineHeight: '15px',
  fontWeight: 600,
}));

export const CompactCountBadge = styled(CountBadge)({
  minWidth: 16,
  minHeight: 16,
  padding: 0,
  background: 'var(--stage-compact-badge-surface)',
  color: 'var(--stage-color)',
  fontSize: 10,
  lineHeight: '12px',
});

export const CardsStack = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 9,
  overflowY: 'auto',
  ...scrollbarStyles,
  marginTop: -7,
  marginRight: -10,
  paddingTop: 0,
  paddingRight: 10,
  paddingBottom: 2,

  '&[data-dragging-over="true"]': {
    outline: '1px dashed var(--stage-color)',
    outlineOffset: 4,
  },

  [forMobile]: {
    gap: 9,
    marginRight: 0,
    paddingRight: 0,
  },
});

export const CardSlot = styled('div')({
  paddingTop: 7,
  boxSizing: 'border-box',

  '&[data-dragging-clone="true"]': {
    pointerEvents: 'none',
  },

  '&[data-dragging-original="true"]': {
    pointerEvents: 'none',
    visibility: 'hidden',
  },
});

export const EmptyColumn = styled('div')(({ theme }) => ({
  minHeight: 88,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px dashed ${theme.colors.white[12]}`,
  borderRadius: 10,
  color: theme.colors.white[40],
}));

export const AddCardSlot = styled('div')({
  paddingTop: 7,
  boxSizing: 'border-box',
});

export const AddCardBox = styled('div')(({ theme }) => ({
  display: 'flex',
  border: `1px dashed ${theme.colors.white[12]}`,
  borderRadius: 10,
  color: theme.colors.white[40],
}));

// Holds a board owner's own control (a follow-up marker, say). Clicks inside must not reach the
// card's own select handler, so the row stops propagation at the DOM level via `pointer-events`
// scoping in the control itself; the row only reserves the space.
export const CardAccessoryRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[4],
  marginTop: theme.spacing[4],
}));

export const CardButton = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: 92,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '12px 12px 12px 16px',
  textAlign: 'left',
  background: pipelineColors.surfaceAlt,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: 10,
  color: theme.colors.text.primary,
  cursor: 'pointer',
  boxShadow: 'none',
  position: 'relative',
  overflow: 'visible',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: 4,
    borderRadius: '10px 0 0 10px',
    background: 'var(--accent-color)',
  },

  '&[data-variant="dashed"]': {
    borderStyle: 'dashed',
  },

  '&[data-accent-tone="blue"]': {
    background: pipelineColors.cardLightBlueSurface,
    borderColor: pipelineColors.cardLightBlueBorder,
  },

  '&[data-accent-tone="amber"]': {
    background: pipelineColors.amberColumnSurface,
    borderColor: pipelineColors.cardAmberBorder,
  },

  '&[data-variant="dashed"][data-accent-tone="neutral"]': {
    paddingLeft: 12,
  },

  '&[data-variant="dashed"][data-accent-tone="neutral"]::before': {
    display: 'none',
  },

  '&[data-mixed-tone="true"]::before': {
    background: `linear-gradient(135deg, ${pipelineColors.amber} 0%, ${pipelineColors.amber} 50%, ${pipelineColors.lightBlue} 50%, ${pipelineColors.lightBlue} 100%)`,
  },

  '&[data-title-tone="amber"] [data-card-title="true"] span': {
    color: pipelineColors.lightYellow,
  },

  '&[data-has-timer="true"] [data-card-title-group="true"]': {
    paddingRight: 44,
  },

  '&[data-is-dragging="true"]': {
    boxShadow: `0 8px 24px ${theme.colors.black[40]}`,
  },

  // Last, and ahead of the accent tones on purpose: these rules carry the same specificity, so
  // source order is what decides. The dragging pair re-states the elevation instead of letting it
  // replace the ring, which would drop the outline for exactly as long as the card is held.
  '&[data-outline-tone]': {
    borderColor: 'var(--outline-color)',
    boxShadow: '0 0 0 1px var(--outline-color)',
  },

  '&[data-outline-tone][data-is-dragging="true"]': {
    boxShadow: `0 0 0 1px var(--outline-color), 0 8px 24px ${theme.colors.black[40]}`,
  },
}));

export const CardHeader = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 8,
  minWidth: 0,
});

export const CardTitleGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  flex: 1,
});

export const CardAvatar = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: theme.radius.full,
  overflow: 'hidden',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.3,
  userSelect: 'none',

  '&[data-icon="briefcase"]': {
    borderRadius: theme.radius[8],
  },

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  '& svg': {
    width: 14,
    height: 14,
  },
}));

export const CardTitleText = styled('span')(({ theme }) => ({
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  color: theme.colors.white[100],
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '15.4px',
  letterSpacing: 0,
}));

export const CardMutedText = styled('span')(({ theme }) => ({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  color: theme.colors.white[40],
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16.8px',
  letterSpacing: 0,

  '&[data-tone="via"]': {
    color: theme.colors.white[60],
  },
}));

export const TextPropertyGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
});

export const BadgeRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  paddingTop: 0,
});

export const CardBadge = PipelineBadge;

export const IndicatorIcon = styled('span')(({ theme }) => ({
  width: 24,
  minWidth: 24,
  height: 21,
  boxSizing: 'border-box',
  padding: '2px 4px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  background: pipelineColors.badgeDark,
  color: theme.colors.white[60],

  svg: {
    width: 16,
    height: 16,
  },
}));

export const TimerBadge = styled(CardBadge)(({ theme }) => ({
  position: 'absolute',
  top: -7,
  right: -4,
  zIndex: 1,
  minHeight: 21,
  height: 21,
  background: theme.colors.brand.solid,
  color: theme.colors.white[100],
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16.8px',
  borderRadius: 999,
  padding: '2px 8px',

  '&[data-tone="amber"]': {
    background: pipelineColors.yellow,
  },

  '&[data-tone="yellow"]': {
    background: pipelineColors.yellow,
  },

  '&[data-tone="red"]': {
    background: theme.colors.error.solid,
    color: pipelineColors.white,
  },
}));

export const StateShell = styled('div')(({ theme }) => ({
  height: '100%',
  minHeight: 360,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 24,
  borderRadius: 8,
  border: `1px dashed ${theme.colors.white[12]}`,
  background: pipelineColors.activeColumnSurface,
  color: theme.colors.white[80],
  textAlign: 'center',
}));
